import useProfileDrafts, { ProfileDraftShelf } from '../useProfileDrafts';
import HiringPreferencePicker from '@/components/HiringPreferencePicker';
import ProfilePhotoGalleryEditor from '../components/ProfilePhotoGalleryEditor';
import { useEffect, useMemo, useRef, useState } from 'react';
import adminApi from '@/lib/adminApi';
import { resolveAdminCityLocation } from '@/lib/adminLocation';
import { collaboratorProfilePath } from '@/lib/collaboratorSlug';
import { artistCategoryNames } from '@/lib/artistCategories';
import {
  defaultCollaboratorCategoryOptions,
  getCollaboratorSubcategoryOptions,
  inferCollaboratorMainCategory,
  mergeCollaboratorCategorySettings,
  toCollaboratorCategoryPayload,
} from '@/lib/collaboratorCategories';
import { analyzeCollaboratorProfile, buildCollaboratorBioSuggestions, profileScoreBadgeClass } from '@/lib/profileCoach';

const MAX_PORTFOLIO_IMAGES = 20;
const MAX_PORTFOLIO_VIDEOS = 2;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const ADMIN_FALLBACK_EMAILS = ['me@kalamanch.art', 'artist@kalamanch.art'];
const collaboratorCategoryOptions = artistCategoryNames.filter((category) => category !== 'Other').slice(0, 36);
const COLLABORATOR_SEO_FIELDS = [
  'seo_slug',
  'meta_title',
  'meta_description',
  'meta_keywords',
  'image_alt_text',
  'image_title',
  'image_caption',
  'og_title',
  'og_description',
];
const emptyCollaboratorForm = {
  name: '',
  mobile: '',
  email: '',
  display_profession: '',
  show_contact_person_public: false,
  seo_slug: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  image_alt_text: '',
  image_title: '',
  image_caption: '',
  og_title: '',
  og_description: '',
  organization_name: '',
  organization_type: 'Production House',
  organization_category: 'Production & Talent Services',
  organization_subcategories: [],
  organization_subcategory_other: '',
  city: '',
  state: '',
  country: 'India',
  website: '',
  instagram: '',
  facebook: '',
  looking_for_text: '',
  image_url: '',
  portfolio_image_urls: ['', '', ''],
  portfolio_video_urls: ['', ''],
  bio: '',
  is_verified: false,
  is_featured: false,
  login_password: '',
  send_login_email: true,
};

const emptyCategoryDraft = {
  name: '',
  subcategories_text: '',
};

const withFixedSlots = (values, count) => [...(Array.isArray(values) ? values : []), ...Array(count).fill('')].slice(0, count);
const splitLookingFor = (value = '') =>
  String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
const formatCollaboratorBioForPublicPage = (value = '') => {
  const bullet = String.fromCharCode(8226);
  return String(value || '')
    .replace(/\r/g, '')
    .replace(/\s+\u2022\s+/g, `\n${bullet} `)
    .replace(/\s+\u00e2\u20ac\u00a2\s+/g, `\n${bullet} `)
    .replace(/(Services\s*&\s*Specializations\s*:)/gi, '\n$1')
    .replace(/(Why\s+Choose[^?]+\?)/gi, '\n$1')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n\n');
};
const hasPushOptIn = (collaborator) =>
  Boolean(collaborator.push_notifications_opted_in || Number(collaborator.push_notification_device_count || 0) > 0);
const hasVerifiedEmail = (account = {}) => account.email_verified === true || account.email_verified_at;
const isCollaboratorReviewReady = (collaborator = {}) => hasVerifiedEmail(collaborator) && analyzeCollaboratorProfile(collaborator).score >= 70;
const socialProfileConfig = [
  { key: 'instagram', label: 'Instagram', shortLabel: 'IG', tone: 'border-pink-200 bg-pink-50 text-pink-800' },
  { key: 'facebook', label: 'Facebook', shortLabel: 'FB', tone: 'border-blue-200 bg-blue-50 text-blue-800' },
  { key: 'youtube', label: 'YouTube', shortLabel: 'YT', tone: 'border-red-200 bg-red-50 text-red-800' },
  { key: 'website', label: 'Website', shortLabel: 'WEB', tone: 'border-slate-200 bg-slate-50 text-slate-800' },
];
const socialProfileDisplayName = (url = '') => {
  try {
    const parsed = new URL(String(url || '').trim());
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    return pathParts[0] ? `@${pathParts[0].replace(/^@/, '')}` : parsed.hostname.replace(/^www\./, '');
  } catch (error) {
    return String(url || '').trim();
  }
};
const formatFollowerCount = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return '';
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1)}M followers`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}K followers`;
  return `${count} follower${count === 1 ? '' : 's'}`;
};
const socialFollowerCount = (account, key) =>
  account[`${key}_followers`] ||
  account[`${key}_followers_count`] ||
  account[`${key}_follower_count`] ||
  account.social_followers?.[key] ||
  account.social_profile_followers?.[key] ||
  '';
const getCollaboratorSocialProfiles = (collaborator) =>
  socialProfileConfig
    .map((profile) => ({
      ...profile,
      url: String(collaborator[profile.key] || '').trim(),
      followerLabel: formatFollowerCount(socialFollowerCount(collaborator, profile.key)),
    }))
    .filter((profile) => profile.url);
const formatPushSync = (value) => {
  if (!value) return 'No active app device';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Device synced';
  return `Last synced ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};
const formatCreatedAt = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const getDaysOnKalamanch = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const diff = Date.now() - date.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};
const buildMilestoneMessage = (collaborator, milestoneLabel) =>
  `Namaste ${collaborator.organization_name || collaborator.name || 'Collaborator'},\n\nCongratulations on completing ${milestoneLabel} with KalaManch.art. We are glad to have you on the platform. Keep your profile updated so artists can understand what you hire, organise, train, or host.\n\nWarm wishes,\nKalaManch Team`;

const CollaboratorActionButton = ({ children, className = '', ...props }) => (
  <button
    type="button"
    className={`rounded-full px-4 py-2 text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {children}
  </button>
);

const isValidUrlForHosts = (value, hosts) => {
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return hosts.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`));
  } catch (error) {
    return false;
  }
};

const isAdminFallbackEmail = (email) =>
  ADMIN_FALLBACK_EMAILS.includes(String(email || '').trim().toLowerCase());

const isValidPublicVideoUrl = (value) => {
  if (!value) return true;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const AdminCollaborators = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [busyCollaboratorId, setBusyCollaboratorId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCollaboratorId, setEditingCollaboratorId] = useState('');
  const [formData, setFormData] = useState(emptyCollaboratorForm);
  const [formBusy, setFormBusy] = useState(false);
  const [seoBusy, setSeoBusy] = useState(false);
  const [aiSeoBusy, setAiSeoBusy] = useState(false);
  const [locationLookupBusy, setLocationLookupBusy] = useState(false);
  const [aiCollaboratorBrief, setAiCollaboratorBrief] = useState('');
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const draftStore = useProfileDrafts('collaborator', showForm, { formData, editingId: editingCollaboratorId, brief: aiCollaboratorBrief });
  const restoreProfileDraft = (draft) => {
    setFormData({ ...emptyCollaboratorForm, ...draft.formData, login_password: '' });
    setEditingCollaboratorId(draft.editingId || '');
    setAiCollaboratorBrief(draft.brief || '');
    setFormError('');
    setShowForm(true);
  };
  const [imageUploading, setImageUploading] = useState(false);
  const [messageCollaboratorId, setMessageCollaboratorId] = useState('');
  const [collaboratorMessage, setCollaboratorMessage] = useState('');
  const [messageNotice, setMessageNotice] = useState('');
  const [lookingForSearch, setLookingForSearch] = useState('');
  const [collaboratorCategoryMap, setCollaboratorCategoryMap] = useState(defaultCollaboratorCategoryOptions);
  const [categoryDraft, setCategoryDraft] = useState(emptyCategoryDraft);
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [categoryNotice, setCategoryNotice] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const lastAutoSeoBioRef = useRef('');

  useEffect(() => {
    if (!showForm) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showForm]);

  const loadCollaborators = async () => {
    const response = await adminApi.get('/admin/collaborators');
    const nextCollaborators = response.data.collaborators || [];
    setCollaborators(nextCollaborators);
    return nextCollaborators;
  };

  const loadCollaboratorCategories = async () => {
    const response = await adminApi.get('/admin/settings/collaborator-categories');
    const merged = mergeCollaboratorCategorySettings(response.data?.settings);
    setCollaboratorCategoryMap(merged);
    return merged;
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [collaboratorResponse, categoryResponse] = await Promise.all([
          adminApi.get('/admin/collaborators'),
          adminApi.get('/admin/settings/collaborator-categories'),
        ]);
        if (mounted) {
          setCollaborators(collaboratorResponse.data.collaborators || []);
          setCollaboratorCategoryMap(mergeCollaboratorCategorySettings(categoryResponse.data?.settings));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const collaboratorTypesInList = useMemo(() => {
    return Array.from(new Set(collaborators.map((item) => item.organization_type).filter(Boolean))).sort();
  }, [collaborators]);
  const collaboratorCategoryEntries = useMemo(() => Object.entries(collaboratorCategoryMap || {}), [collaboratorCategoryMap]);
  const collaboratorCategoryNames = useMemo(() => collaboratorCategoryEntries.map(([name]) => name), [collaboratorCategoryEntries]);
  const instituteAcademyTypes = useMemo(
    () => collaboratorCategoryMap['Institutes & Academies'] || [],
    [collaboratorCategoryMap]
  );
  const studioVenueTypes = useMemo(
    () => collaboratorCategoryMap['Studios & Venues'] || [],
    [collaboratorCategoryMap]
  );
  const filteredCollaboratorCategoryOptions = useMemo(() => {
    const query = lookingForSearch.trim().toLowerCase();
    return collaboratorCategoryOptions.filter((category) => !query || category.toLowerCase().includes(query));
  }, [lookingForSearch]);

  const summary = useMemo(() => ({
    total: collaborators.length,
    verified: collaborators.filter((item) => item.is_verified).length,
    featured: collaborators.filter((item) => item.is_featured).length,
    institutes: collaborators.filter((item) => instituteAcademyTypes.includes(item.organization_type)).length,
    studios: collaborators.filter((item) => studioVenueTypes.includes(item.organization_type)).length,
    withWebsite: collaborators.filter((item) => item.website).length,
    fromApp: collaborators.filter((item) => item.sync_source === 'android_app').length,
    pushOptedIn: collaborators.filter(hasPushOptIn).length,
    pushActiveDevices: collaborators.reduce((total, item) => total + Number(item.push_notification_device_count || 0), 0),
  }), [collaborators, instituteAcademyTypes, studioVenueTypes]);

  const filteredCollaborators = useMemo(() => {
    const query = search.trim().toLowerCase();
    return collaborators.filter((collaborator) => {
      const matchesQuery =
        !query ||
        [
          collaborator.name,
          collaborator.email,
          collaborator.mobile,
          collaborator.organization_name,
          collaborator.organization_type,
          collaborator.city,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'verified' && collaborator.is_verified) ||
        (statusFilter === 'unverified' && !collaborator.is_verified) ||
        (statusFilter === 'pending_email' && !hasVerifiedEmail(collaborator)) ||
        (statusFilter === 'review_ready' && !collaborator.is_verified && isCollaboratorReviewReady(collaborator)) ||
        (statusFilter === 'incomplete_signup' && !collaborator.is_verified && !isCollaboratorReviewReady(collaborator)) ||
        (statusFilter === 'featured' && collaborator.is_featured) ||
        (statusFilter === 'push_on' && hasPushOptIn(collaborator)) ||
        (statusFilter === 'push_off' && !hasPushOptIn(collaborator)) ||
        (statusFilter === 'standard' && !collaborator.is_featured);

      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'institutes' && instituteAcademyTypes.includes(collaborator.organization_type)) ||
        (typeFilter === 'studios' && studioVenueTypes.includes(collaborator.organization_type)) ||
        collaborator.organization_type === typeFilter;

      return matchesQuery && matchesStatus && matchesType;
    });
  }, [collaborators, search, statusFilter, typeFilter, instituteAcademyTypes, studioVenueTypes]);

  const resetForm = ({ keepNotice = false } = {}) => {
    if (keepNotice) draftStore.complete();
    else if (showForm && !draftStore.save()) return;
    setFormData(emptyCollaboratorForm);
    setAiCollaboratorBrief('');
    lastAutoSeoBioRef.current = '';
    setEditingCollaboratorId('');
    setShowForm(false);
    setFormError('');
    if (!keepNotice) setFormNotice('');
  };

  const handleFormChange = (field, value) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleOrganizationCategoryChange = (value) => {
    setFormError('');
    setFormNotice('');
    const firstSubcategory = getCollaboratorSubcategoryOptions(collaboratorCategoryMap, value)[0] || '';
    setFormData((current) => ({
      ...current,
      organization_category: value,
      organization_subcategories: firstSubcategory ? [firstSubcategory] : [],
      organization_subcategory_other: '',
      organization_type: firstSubcategory || value,
    }));
  };

  const toggleOrganizationSubcategory = (subcategory) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => {
      const selected = Array.isArray(current.organization_subcategories) ? current.organization_subcategories : [];
      const exists = selected.some((item) => item.toLowerCase() === subcategory.toLowerCase());
      const nextSubcategories = exists
        ? selected.filter((item) => item.toLowerCase() !== subcategory.toLowerCase())
        : [...selected, subcategory];
      return {
        ...current,
        organization_subcategories: nextSubcategories,
        organization_type: toCollaboratorCategoryPayload({
          ...current,
          organization_subcategories: nextSubcategories,
        }).organization_type,
      };
    });
  };

  const toggleLookingForCategory = (category) => {
    setFormError('');
    setFormNotice('');
    setFormData((current) => {
      const selected = splitLookingFor(current.looking_for_text);
      const exists = selected.some((item) => item.toLowerCase() === category.toLowerCase());
      const next = exists
        ? selected.filter((item) => item.toLowerCase() !== category.toLowerCase())
        : [...selected, category];
      return { ...current, looking_for_text: next.join(', ') };
    });
  };

  const handleSlotChange = (field, index, value) => {
    setFormData((current) => {
      const limit = field === 'portfolio_image_urls' ? MAX_PORTFOLIO_IMAGES : MAX_PORTFOLIO_VIDEOS;
      const nextValues = [...withFixedSlots(current[field], limit)];
      nextValues[index] = value;
      return { ...current, [field]: nextValues };
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const uploadPayload = new FormData();
    uploadPayload.append('file', file);
    setImageUploading(true);
    setFormError('');
    try {
      const response = await adminApi.post('/uploads/profile-image', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((current) => ({ ...current, image_url: response.data.image_url || '' }));
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to upload profile image.');
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const handlePortfolioImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const uploadPayload = new FormData();
    uploadPayload.append('file', file);
    setImageUploading(true);
    setFormError('');
    try {
      const response = await adminApi.post('/uploads/profile-image', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleSlotChange('portfolio_image_urls', index, response.data.image_url || '');
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to upload portfolio photo.');
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const handleEdit = (collaborator) => {
    draftStore.start();
    setEditingCollaboratorId(collaborator.id);
    setAiCollaboratorBrief('');
    lastAutoSeoBioRef.current = COLLABORATOR_SEO_FIELDS.some((field) => String(collaborator[field] || '').trim())
      ? collaborator.bio || ''
      : '';
    setFormData({
      name: collaborator.name || '',
      mobile: collaborator.mobile || '',
      email: collaborator.email || '',
      display_profession: collaborator.display_profession || '',
      show_contact_person_public: Boolean(collaborator.show_contact_person_public),
      seo_slug: collaborator.seo_slug || collaborator.slug || '',
      meta_title: collaborator.meta_title || '',
      meta_description: collaborator.meta_description || '',
      meta_keywords: collaborator.meta_keywords || '',
      image_alt_text: collaborator.image_alt_text || '',
      image_title: collaborator.image_title || '',
      image_caption: collaborator.image_caption || '',
      og_title: collaborator.og_title || '',
      og_description: collaborator.og_description || '',
      organization_name: collaborator.organization_name || '',
      organization_type: collaborator.organization_type || 'Production House',
      organization_category:
        collaborator.organization_category ||
        inferCollaboratorMainCategory(
          collaboratorCategoryMap,
          Array.isArray(collaborator.organization_subcategories) && collaborator.organization_subcategories.length
            ? collaborator.organization_subcategories
            : splitLookingFor(collaborator.organization_type || '')
        ) ||
        'Production & Talent Services',
      organization_subcategories: Array.isArray(collaborator.organization_subcategories) && collaborator.organization_subcategories.length
        ? collaborator.organization_subcategories
        : splitLookingFor(collaborator.organization_type || ''),
      organization_subcategory_other: collaborator.organization_subcategory_other || '',
      city: collaborator.city || '',
      state: collaborator.state || '',
      country: collaborator.country || 'India',
      website: collaborator.website || '',
      instagram: collaborator.instagram || '',
      facebook: collaborator.facebook || '',
      looking_for_text: Array.isArray(collaborator.looking_for) ? collaborator.looking_for.join(', ') : '',
      image_url: collaborator.image_url || '',
      portfolio_image_urls: withFixedSlots(collaborator.portfolio_image_urls, MAX_PORTFOLIO_IMAGES),
      portfolio_video_urls: withFixedSlots(collaborator.portfolio_video_urls, MAX_PORTFOLIO_VIDEOS),
      bio: collaborator.bio || '',
      is_verified: Boolean(collaborator.is_verified),
      is_featured: Boolean(collaborator.is_featured),
      login_password: '',
      send_login_email: true,
    });
    setFormError('');
    setFormNotice('');
    setShowForm(true);
  };

  const handleCreate = () => {
    draftStore.start();
    setEditingCollaboratorId('');
    setAiCollaboratorBrief('');
    lastAutoSeoBioRef.current = '';
    setFormData(emptyCollaboratorForm);
    setFormError('');
    setFormNotice('');
    setShowForm(true);
  };

  const handleCityBlur = async () => {
    const requestedCity = formData.city.trim();
    const stateBeforeLookup = formData.state.trim();
    const countryBeforeLookup = formData.country.trim();
    if (!requestedCity) return;
    setLocationLookupBusy(true);
    try {
      const locationResult = await resolveAdminCityLocation(requestedCity, formData.country);
      if (!locationResult) return;
      setFormData((current) => {
        if (current.city.trim().toLowerCase() !== requestedCity.toLowerCase()) return current;
        return {
          ...current,
          state: current.state.trim() === stateBeforeLookup ? locationResult.state || current.state : current.state,
          country: current.country.trim() === countryBeforeLookup ? locationResult.country || current.country || 'India' : current.country,
        };
      });
    } catch (error) {
      // Keep State and Country editable when automatic lookup is unavailable.
    } finally {
      setLocationLookupBusy(false);
    }
  };

  const buildCollaboratorPayload = (source = formData) => ({
    ...source,
    display_profession: String(source.display_profession || '').trim(),
    show_contact_person_public: Boolean(source.show_contact_person_public),
    bio: formatCollaboratorBioForPublicPage(source.bio),
    ...toCollaboratorCategoryPayload(source),
    looking_for: source.looking_for_text.split(',').map((value) => value.trim()).filter(Boolean),
    portfolio_image_urls: source.portfolio_image_urls.map((value) => value.trim()).filter(Boolean),
    portfolio_video_urls: source.portfolio_video_urls.map((value) => value.trim()).filter(Boolean),
    login_password: (source.login_password || '').trim(),
    send_login_email: Boolean(source.send_login_email),
  });

  const generateCollaboratorBioSeo = async ({
    generateBio = false,
    bioOverride = '',
    automatic = false,
    overwriteSeo = false,
  } = {}) => {
    const sourceBio = String(bioOverride || formData.bio || '').trim();
    const categoryPayload = toCollaboratorCategoryPayload(formData);
    if (!formData.organization_name.trim() || !categoryPayload.organization_type?.trim() || !formData.city.trim()) {
      if (!automatic) setFormError('Add the organization name, collaborator type, and city before using ChatGPT.');
      return;
    }
    if (generateBio && (!formData.state.trim() || !formData.country.trim())) {
      if (!automatic) setFormError('Add the collaborator city, state, and country before generating the Bio.');
      return;
    }
    if (sourceBio.length < 40) {
      if (!automatic) {
        setFormError(generateBio ? 'Add fuller collaborator details in the ChatGPT box first.' : 'Add a fuller collaborator bio before using ChatGPT for SEO.');
      }
      return;
    }
    if (automatic && (COLLABORATOR_SEO_FIELDS.some((field) => String(formData[field] || '').trim()) || lastAutoSeoBioRef.current === sourceBio)) {
      return;
    }

    setAiSeoBusy(true);
    setFormError('');
    setFormNotice(automatic ? 'Creating SEO fields from the collaborator bio...' : 'ChatGPT is creating the collaborator bio and SEO fields...');
    try {
      const response = await adminApi.post('/admin/collaborators/ai-seo-draft', {
        organization_name: formData.organization_name,
        organization_type: categoryPayload.organization_type,
        organization_category: categoryPayload.organization_category,
        organization_subcategories: categoryPayload.organization_subcategories,
        display_profession: formData.display_profession,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        looking_for: splitLookingFor(formData.looking_for_text),
        bio: sourceBio,
        website: formData.website,
        instagram: formData.instagram,
        generate_bio: generateBio,
      });
      const generated = response.data?.seo || {};
      setFormData((current) => {
        const next = { ...current, bio: generateBio ? generated.bio || sourceBio : sourceBio };
        COLLABORATOR_SEO_FIELDS.forEach((field) => {
          if (overwriteSeo || !String(current[field] || '').trim()) next[field] = generated[field] || current[field] || '';
        });
        return next;
      });
      lastAutoSeoBioRef.current = generateBio ? generated.bio || sourceBio : sourceBio;
      setFormNotice(
        generateBio
          ? 'ChatGPT created the collaborator bio and filled URL, metadata, image SEO, and Open Graph fields. Review them, then save.'
          : 'SEO fields were filled automatically from the collaborator bio. Review them, then save.'
      );
    } catch (error) {
      setFormError(error.response?.data?.detail || 'ChatGPT could not create the collaborator SEO fields right now.');
      setFormNotice('');
    } finally {
      setAiSeoBusy(false);
    }
  };

  const handleBioBlur = () => {
    generateCollaboratorBioSeo({ automatic: true });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormBusy(true);
    setFormError('');
    setFormNotice('');

    try {
      const payload = buildCollaboratorPayload();

      if (!payload.name.trim()) throw new Error('Contact person is required.');
      if (!payload.organization_name.trim()) throw new Error('Organization name is required.');
      if (!payload.organization_category?.trim()) throw new Error('Main collaborator category is required.');
      if (!payload.organization_type?.trim()) throw new Error('Select at least one collaborator sub-category or add another one.');
      if (!payload.city?.trim()) throw new Error('City is required.');
      if (!payload.state?.trim()) throw new Error('State is required.');
      if (!payload.country?.trim()) throw new Error('Country is required.');
      if (!emailPattern.test(payload.email.trim())) throw new Error('Please enter a valid email address.');
      if (payload.login_password && isAdminFallbackEmail(payload.email)) {
        throw new Error("Use the collaborator's real email before creating login credentials.");
      }
      if (!payload.bio.trim()) throw new Error('Profile bio is required.');
      if (!isValidUrlForHosts(payload.instagram, ['instagram.com', 'www.instagram.com'])) {
        throw new Error('Please enter a valid Instagram profile URL.');
      }
      if (!isValidUrlForHosts(payload.facebook, ['facebook.com', 'www.facebook.com', 'fb.com', 'm.facebook.com'])) {
        throw new Error('Please enter a valid Facebook page URL.');
      }
      if (payload.portfolio_video_urls.some((value) => !isValidPublicVideoUrl(value))) {
        throw new Error('Portfolio videos must be valid public video URLs.');
      }

      if (editingCollaboratorId) {
        await adminApi.put(`/admin/collaborators/${editingCollaboratorId}`, payload);
        resetForm({ keepNotice: true });
        setFormNotice('Collaborator updated successfully.');
      } else {
        await adminApi.post('/admin/collaborators', payload);
        resetForm({ keepNotice: true });
        setFormNotice('Collaborator created successfully.');
      }
      await loadCollaborators();
    } catch (error) {
      if (!error.response) {
        setFormError(error.message || 'Unable to save collaborator profile.');
      } else if (error.response?.status === 404) {
        setFormError('Collaborator admin save route is not live yet on the backend. Redeploy Render after pushing backend changes.');
      } else {
        setFormError(error.response?.data?.detail || 'Unable to save collaborator profile.');
      }
    } finally {
      setFormBusy(false);
    }
  };

  const handleSeoSave = async () => {
    if (!editingCollaboratorId) {
      setFormNotice('Create the collaborator first, then reopen it to save custom SEO fields.');
      return;
    }
    setSeoBusy(true);
    setFormError('');
    setFormNotice('');
    const seoPayload = {
      seo_slug: formData.seo_slug || '',
      meta_title: formData.meta_title || '',
      meta_description: formData.meta_description || '',
      meta_keywords: formData.meta_keywords || '',
      image_alt_text: formData.image_alt_text || '',
      image_title: formData.image_title || '',
      image_caption: formData.image_caption || '',
      og_title: formData.og_title || '',
      og_description: formData.og_description || '',
    };
    try {
      await adminApi.patch(`/admin/collaborators/${editingCollaboratorId}/seo`, seoPayload);
      await loadCollaborators();
      setFormNotice('Dedicated collaborator SEO route live. Collaborator SEO fields saved.');
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          await adminApi.put(`/admin/collaborators/${editingCollaboratorId}`, buildCollaboratorPayload());
          await loadCollaborators();
          setFormNotice('Saved through fallback route. The dedicated collaborator SEO route is not live on the backend yet, but your SEO values were saved through the main collaborator update route.');
          return;
        } catch (fallbackError) {
          setFormError(fallbackError.response?.data?.detail || fallbackError.message || 'Unable to save collaborator SEO fields.');
          return;
        }
      }
      setFormError(error.response?.data?.detail || error.message || 'Unable to save collaborator SEO fields.');
    } finally {
      setSeoBusy(false);
    }
  };

  const handleCategoryDraftChange = (field, value) => {
    setCategoryError('');
    setCategoryNotice('');
    setCategoryDraft((current) => ({ ...current, [field]: value }));
  };

  const handleCategorySave = async () => {
    const name = String(categoryDraft.name || '').trim();
    const subcategories = splitLookingFor(categoryDraft.subcategories_text);
    if (!name) {
      setCategoryError('Main collaborator category name is required.');
      return;
    }
    if (!subcategories.length) {
      setCategoryError('Add at least one sub-category.');
      return;
    }
    setCategoryBusy(true);
    setCategoryError('');
    setCategoryNotice('');
    try {
      const currentSettings = collaboratorCategoryEntries.map(([entryName, values]) => ({
        name: entryName,
        subcategories: values,
      }));
      const nextSettings = {
        categories: [...currentSettings, { name, subcategories }],
      };
      await adminApi.put('/admin/settings/collaborator-categories', nextSettings);
      await loadCollaboratorCategories();
      setCategoryDraft(emptyCategoryDraft);
      setCategoryNotice('Collaborator category settings updated.');
    } catch (error) {
      setCategoryError(error.response?.data?.detail || 'Unable to save collaborator category settings.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleResendEmailVerification = async (collaborator) => {
    setBusyCollaboratorId(collaborator.id);
    setMessageNotice('');
    try {
      const response = await adminApi.post(`/admin/collaborators/${collaborator.id}/resend-email-verification`);
      setMessageNotice(response.data?.message || 'Verification email sent.');
    } catch (error) {
      setMessageNotice(error.response?.data?.detail || 'Verification email could not be sent.');
    } finally {
      await loadCollaborators();
      setBusyCollaboratorId('');
    }
  };

  const handleVerify = async (collaboratorId, isVerified) => {
    setBusyCollaboratorId(collaboratorId);
    try {
      await adminApi.patch(`/admin/collaborators/${collaboratorId}/${isVerified ? 'unverify' : 'verify'}`);
      await loadCollaborators();
    } finally {
      setBusyCollaboratorId('');
    }
  };

  const handleFeature = async (collaboratorId, isFeatured) => {
    setBusyCollaboratorId(collaboratorId);
    try {
      await adminApi.patch(`/admin/collaborators/${collaboratorId}/${isFeatured ? 'unfeature' : 'feature'}`);
      await loadCollaborators();
    } finally {
      setBusyCollaboratorId('');
    }
  };

  const handleDelete = async (collaboratorId) => {
    if (!window.confirm('Delete this collaborator and related conversations?')) return;
    setBusyCollaboratorId(collaboratorId);
    try {
      await adminApi.delete(`/admin/collaborators/${collaboratorId}`);
      await loadCollaborators();
    } finally {
      setBusyCollaboratorId('');
    }
  };

  const handleSendAdminMessage = async (collaboratorId) => {
    if (!collaboratorMessage.trim()) return;
    setBusyCollaboratorId(collaboratorId);
    try {
      await adminApi.post(`/admin/collaborators/${collaboratorId}/messages`, {
        subject: 'Profile review note',
        message: collaboratorMessage.trim(),
      });
      setMessageCollaboratorId('');
      setCollaboratorMessage('');
      setMessageNotice('Message sent to collaborator inbox and email.');
    } catch (error) {
      setMessageNotice(error.response?.data?.detail || 'Unable to send message right now.');
    } finally {
      setBusyCollaboratorId('');
    }
  };

  return (
    <div className="space-y-6">
      <ProfileDraftShelf store={draftStore} onRestore={restoreProfileDraft} open={showForm} />
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/55 px-4 py-6">
          <section className="max-h-[calc(100vh-3rem)] w-full max-w-5xl overflow-y-auto rounded-[2rem] border border-orange-100 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-orange-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#FF6F00]">Collaborator Admin</p>
                <h3 className="mt-2 text-2xl font-bold text-gray-900">
                  {editingCollaboratorId ? 'Edit Collaborator' : 'Create Collaborator'}
                </h3>
              </div>
              <button type="button" className="btn-outline" onClick={() => resetForm()} disabled={formBusy || imageUploading}>Close</button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-5 px-6 py-6">
              <div className="grid gap-4 md:grid-cols-2">
                <input value={formData.name} onChange={(event) => handleFormChange('name', event.target.value)} placeholder="Contact person" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                <input value={formData.email} onChange={(event) => handleFormChange('email', event.target.value)} type="email" placeholder="Email address" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                <input value={formData.mobile} onChange={(event) => handleFormChange('mobile', event.target.value)} placeholder="Mobile (optional)" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
                <input value={formData.organization_name} onChange={(event) => handleFormChange('organization_name', event.target.value)} placeholder="Organization name" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                <select value={formData.organization_category} onChange={(event) => handleOrganizationCategoryChange(event.target.value)} className="w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white">
                  {collaboratorCategoryNames.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
                <input value={formData.city} onChange={(event) => handleFormChange('city', event.target.value)} onBlur={handleCityBlur} placeholder="City" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                <input value={formData.state} onChange={(event) => handleFormChange('state', event.target.value)} placeholder="State" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                <input value={formData.country} onChange={(event) => handleFormChange('country', event.target.value)} placeholder="Country" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                <p className="text-xs text-gray-500 md:col-span-2 -mt-2">
                  {locationLookupBusy ? 'Finding State and Country...' : 'State and Country fill automatically after you leave the City box. You can still edit them.'}
                </p>
                <input value={formData.website} onChange={(event) => handleFormChange('website', event.target.value)} placeholder="Website URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
                <input value={formData.instagram} onChange={(event) => handleFormChange('instagram', event.target.value)} placeholder="Instagram profile URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3" />
                <input value={formData.facebook} onChange={(event) => handleFormChange('facebook', event.target.value)} placeholder="Facebook page URL" className="w-full rounded-2xl border border-gray-300 px-4 py-3 md:col-span-2" />
                <input value={formData.organization_subcategory_other} onChange={(event) => handleFormChange('organization_subcategory_other', event.target.value)} placeholder="Other sub-category (optional)" className="w-full rounded-2xl border border-gray-300 px-4 py-3 md:col-span-2" />
                <label className="flex items-start gap-3 rounded-2xl border border-teal-100 bg-teal-50/60 p-4 text-sm font-semibold text-gray-800 md:col-span-2">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.show_contact_person_public)}
                    onChange={(event) => handleFormChange('show_contact_person_public', event.target.checked)}
                    className="mt-1 h-4 w-4"
                  />
                  <span>
                    Show contact person name under company name on public collaborator profile.
                    <span className="mt-1 block text-xs font-normal text-gray-600">Keep unchecked when the public profile should show only the company/organization name.</span>
                  </span>
                </label>
                <div className="rounded-2xl border border-sky-100 bg-sky-50/60 p-4 md:col-span-2">
                  <p className="text-sm font-bold text-sky-950">Main public profession label</p>
                  <p className="mt-1 text-xs text-sky-900">Admin-only field. This short line can show values like Production House, NGO / Arts Foundation, Dance Academy, or Event Venue on public cards.</p>
                  <input
                    value={formData.display_profession}
                    onChange={(event) => handleFormChange('display_profession', event.target.value)}
                    placeholder="Example: Production House, Dance Academy, Event Venue, Talent Agency"
                    className="mt-3 w-full rounded-2xl border border-sky-200 bg-white px-4 py-3"
                  />
                </div>
                {getCollaboratorSubcategoryOptions(collaboratorCategoryMap, formData.organization_category).length > 0 && (
                  <div className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4 md:col-span-2">
                    <p className="text-sm font-bold text-gray-900">Select collaborator sub-categories</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {getCollaboratorSubcategoryOptions(collaboratorCategoryMap, formData.organization_category).map((subcategory) => {
                        const selected = (formData.organization_subcategories || []).includes(subcategory);
                        return (
                          <button
                            key={subcategory}
                            type="button"
                            onClick={() => toggleOrganizationSubcategory(subcategory)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                              selected
                                ? 'border-[#4A148C] bg-[#4A148C] text-white shadow'
                                : 'border-purple-100 bg-white text-gray-700 hover:border-[#4A148C]'
                            }`}
                          >
                            {subcategory}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <input value={formData.looking_for_text} onChange={(event) => handleFormChange('looking_for_text', event.target.value)} placeholder="Looking for (comma separated)" className="w-full rounded-2xl border border-gray-300 px-4 py-3 md:col-span-2" />
                <HiringPreferencePicker value={formData.looking_for_text} onChange={value => handleFormChange('looking_for_text', value)} />
              </div>

              <div className="rounded-3xl border border-sky-100 bg-sky-50/80 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-bold text-sky-950">Collaborator login credentials</p>
                    <p className="mt-1 text-xs leading-5 text-sky-900">
                      Optional. Use the collaborator's real email above and set a temporary password. They must verify email before login, then can change password from My Account.
                    </p>
                  </div>
                  {editingCollaboratorId && (
                    <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold text-sky-800">
                      Leave blank to keep current password
                    </span>
                  )}
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
                  <input
                    type="password"
                    value={formData.login_password || ''}
                    onChange={(event) => handleFormChange('login_password', event.target.value)}
                    placeholder={editingCollaboratorId ? 'New temporary password (optional)' : 'Temporary password for collaborator login'}
                    className="w-full rounded-2xl border border-sky-200 bg-white px-4 py-3"
                    autoComplete="new-password"
                  />
                  <label className="inline-flex items-center gap-3 rounded-2xl border border-sky-200 bg-white px-4 py-3 text-sm font-bold text-sky-950">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.send_login_email)}
                      onChange={(event) => handleFormChange('send_login_email', event.target.checked)}
                    />
                    Email login details
                  </label>
                </div>
                <p className="mt-2 text-xs text-sky-900">
                  Password must be at least 8 characters. The backend stores only a secure hash, not the plain password.
                </p>
              </div>

              <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
                <div className="space-y-4">
                  <textarea value={formData.bio} onChange={(event) => handleFormChange('bio', event.target.value)} onBlur={handleBioBlur} rows="7" placeholder="Collaborator bio" className="w-full rounded-2xl border border-gray-300 px-4 py-3" required />
                  <div className="rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-orange-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-bold text-purple-900">AI Bio + SEO Helper</p>
                        <p className="mt-1 text-xs leading-5 text-gray-600">ChatGPT creates a detailed formatted collaborator profile with specialization and highlight bullet points, then fills the SEO URL, search metadata, image SEO, and Open Graph fields. Nothing is saved until you save the collaborator.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => generateCollaboratorBioSeo({ generateBio: true, bioOverride: aiCollaboratorBrief, overwriteSeo: true })}
                        disabled={aiSeoBusy || formBusy}
                        className="w-fit rounded-full bg-purple-700 px-4 py-2 text-xs font-extrabold text-white transition hover:bg-purple-800 disabled:cursor-wait disabled:opacity-60"
                      >
                        {aiSeoBusy ? 'Creating...' : 'Create Bio + SEO with ChatGPT'}
                      </button>
                    </div>
                    <div className="mt-4 rounded-2xl border border-purple-100 bg-white/90 p-3">
                      <label className="text-xs font-extrabold uppercase tracking-[0.16em] text-purple-800" htmlFor="admin-collaborator-ai-brief">
                        Collaborator details for ChatGPT
                      </label>
                      <textarea
                        id="admin-collaborator-ai-brief"
                        value={aiCollaboratorBrief}
                        onChange={(event) => setAiCollaboratorBrief(event.target.value)}
                        rows="7"
                        placeholder="Paste rough collaborator details here: services, industries, experience, locations served, artist categories required, notable projects, facilities, team strengths, and any confirmed facts that must appear. ChatGPT will create the finished Bio and SEO fields."
                        className="mt-2 w-full rounded-2xl border border-purple-200 bg-white px-4 py-3 text-sm leading-6 text-gray-800 outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                      />
                      <p className="mt-2 text-xs leading-5 text-gray-600">Only include confirmed facts. ChatGPT will not add clients, projects, locations, services, or availability unless you provide them.</p>
                    </div>
                    <div className="mt-3 grid gap-3 lg:grid-cols-3">
                      {buildCollaboratorBioSuggestions(formData).map((suggestion) => (
                        <div key={suggestion.label} className="rounded-2xl bg-white/85 p-3 shadow-sm">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-gray-900">{suggestion.label}</p>
                            <button
                              type="button"
                              onClick={() => generateCollaboratorBioSeo({ bioOverride: suggestion.text })}
                              className="rounded-full border border-purple-200 px-3 py-1 text-xs font-bold text-purple-700 hover:bg-purple-50"
                            >
                              Use + fill SEO
                            </button>
                          </div>
                          <p className="mt-2 text-xs leading-relaxed text-gray-600">{suggestion.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ProfilePhotoGalleryEditor value={formData.portfolio_image_urls} onChange={photos => handleFormChange('portfolio_image_urls', photos)} onBusyChange={setImageUploading} />
                  <div className="rounded-3xl border border-purple-100 bg-purple-50/60 p-4">
                    <p className="text-sm font-bold text-gray-900">Portfolio videos</p>
                    <p className="mt-1 text-xs text-gray-500">Add up to {MAX_PORTFOLIO_VIDEOS} public video links: YouTube, Instagram, Facebook, Vimeo, or similar.</p>
                    <div className="mt-3 space-y-3">
                      {withFixedSlots(formData.portfolio_video_urls, MAX_PORTFOLIO_VIDEOS).map((value, index) => (
                        <input key={`collaborator-video-${index}`} value={value} onChange={(event) => handleSlotChange('portfolio_video_urls', index, event.target.value)} placeholder={`Public video ${index + 1} URL`} className="w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white" />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-3xl border border-orange-100 bg-orange-50/70 p-4">
                    <p className="text-sm font-bold text-gray-900">Profile image</p>
                    <input value={formData.image_url} onChange={(event) => handleFormChange('image_url', event.target.value)} placeholder="Profile image URL" className="mt-3 w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white" />
                    <label className="mt-3 block rounded-2xl border border-orange-200 bg-white px-4 py-3 text-center text-sm font-semibold text-[#FF6F00] cursor-pointer">
                      {imageUploading ? 'Uploading...' : 'Upload image'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                    {formData.image_url && (
                      <img src={formData.image_url} alt="Collaborator preview" className="mt-4 h-36 w-full rounded-2xl border border-gray-200 bg-white object-contain" />
                    )}
                  </div>
                  <div className="rounded-3xl border border-slate-100 bg-slate-50/70 p-4">
                    <p className="text-sm font-bold text-gray-900">Review toggles</p>
                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                      <label className="flex items-center gap-3">
                        <input type="checkbox" checked={formData.is_verified} onChange={(event) => handleFormChange('is_verified', event.target.checked)} />
                        Verified
                      </label>
                      <label className="flex items-center gap-3">
                        <input type="checkbox" checked={formData.is_featured} onChange={(event) => handleFormChange('is_featured', event.target.checked)} />
                        Featured
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-950">Collaborator public page SEO</p>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      These fields control the collaborator page title, meta description, image metadata, and social sharing text. Leave blank to use automatic SEO.
                    </p>
                  </div>
                  <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700">
                    Works for existing collaborator pages too
                  </span>
                </div>
                <div className="mt-4 grid gap-3">
                  <input
                    value={formData.seo_slug || ''}
                    onChange={(event) => handleFormChange('seo_slug', event.target.value)}
                    placeholder="SEO URL slug, e.g. mahakay-events-entertainment-delhi"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                  />
                  <input
                    value={formData.meta_title || ''}
                    onChange={(event) => handleFormChange('meta_title', event.target.value)}
                    placeholder="Meta page title, e.g. Buzz Stage Production House in Noida"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                  />
                  <textarea
                    value={formData.meta_description || ''}
                    onChange={(event) => handleFormChange('meta_description', event.target.value)}
                    rows="3"
                    placeholder="Meta description shown in Google search results"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                  />
                  <textarea
                    value={formData.meta_keywords || ''}
                    onChange={(event) => handleFormChange('meta_keywords', event.target.value)}
                    rows="2"
                    placeholder="Meta keywords, comma separated"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                  />
                  <div className="grid gap-3 md:grid-cols-2">
                    <input
                      value={formData.image_alt_text || ''}
                      onChange={(event) => handleFormChange('image_alt_text', event.target.value)}
                      placeholder="Image ALT text for profile/logo image"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                    />
                    <input
                      value={formData.image_title || ''}
                      onChange={(event) => handleFormChange('image_title', event.target.value)}
                      placeholder="Image title"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                    />
                  </div>
                  <textarea
                    value={formData.image_caption || ''}
                    onChange={(event) => handleFormChange('image_caption', event.target.value)}
                    rows="2"
                    placeholder="Image caption for search/social previews"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                  />
                  <div className="grid gap-3 md:grid-cols-2">
                    <input
                      value={formData.og_title || ''}
                      onChange={(event) => handleFormChange('og_title', event.target.value)}
                      placeholder="Open Graph title for WhatsApp/Facebook/LinkedIn"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                    />
                    <textarea
                      value={formData.og_description || ''}
                      onChange={(event) => handleFormChange('og_description', event.target.value)}
                      rows="2"
                      placeholder="Open Graph description for social sharing cards"
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3"
                    />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => generateCollaboratorBioSeo({ overwriteSeo: true })}
                    disabled={aiSeoBusy || formBusy}
                  >
                    {aiSeoBusy ? 'Generating SEO...' : 'Regenerate SEO from Bio'}
                  </button>
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={handleSeoSave}
                    disabled={seoBusy || aiSeoBusy || formBusy}
                  >
                    {seoBusy ? 'Saving SEO...' : 'Save SEO Fields'}
                  </button>
                  {!editingCollaboratorId && (
                    <p className="text-xs font-semibold text-slate-600">
                      Save the collaborator profile first, then reopen it to store custom SEO values.
                    </p>
                  )}
                </div>
              </div>

              {draftStore.error && <p role="alert" className="text-sm text-red-700">{draftStore.error}</p>}
              {formError && <p className="text-sm text-red-600">{formError}</p>}
              {formNotice && <p className="text-sm text-green-700">{formNotice}</p>}

              <div className="flex flex-wrap gap-3">
                <button type="submit" className="btn-primary" disabled={formBusy || imageUploading}>
                  {formBusy ? 'Saving...' : editingCollaboratorId ? 'Save Collaborator Changes' : 'Create Collaborator'}
                </button>
                <button type="button" className="btn-outline" onClick={() => resetForm()} disabled={formBusy || imageUploading}>
                  Cancel
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Collaborator management</h2>
            <p className="mt-3 text-gray-600">
              Review, verify, feature, message, and update collaborator profiles from the website and Android app.
            </p>
          </div>
          <button type="button" onClick={handleCreate} className="btn-primary">
            Add Collaborator
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-8 gap-4">
        <div className="bg-white rounded-[1.75rem] border border-orange-100 shadow-sm p-5">
          <p className="text-sm text-gray-500">Total collaborators</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{summary.total}</p>
        </div>
        <div className="bg-white rounded-[1.75rem] border border-green-100 shadow-sm p-5">
          <p className="text-sm text-gray-500">Verified</p>
          <p className="text-3xl font-bold text-green-700 mt-2">{summary.verified}</p>
        </div>
        <div className="bg-white rounded-[1.75rem] border border-amber-100 shadow-sm p-5">
          <p className="text-sm text-gray-500">Featured</p>
          <p className="text-3xl font-bold text-amber-700 mt-2">{summary.featured}</p>
        </div>
        <button
          type="button"
          onClick={() => setTypeFilter('institutes')}
          className="bg-white rounded-[1.75rem] border border-emerald-100 shadow-sm p-5 text-left transition hover:bg-emerald-50"
        >
          <p className="text-sm text-gray-500">Institutes & Academies</p>
          <p className="text-3xl font-bold text-emerald-700 mt-2">{summary.institutes}</p>
        </button>
        <button
          type="button"
          onClick={() => setTypeFilter('studios')}
          className="bg-white rounded-[1.75rem] border border-fuchsia-100 shadow-sm p-5 text-left transition hover:bg-fuchsia-50"
        >
          <p className="text-sm text-gray-500">Studios & Venues</p>
          <p className="text-3xl font-bold text-fuchsia-700 mt-2">{summary.studios}</p>
        </button>
        <div className="bg-white rounded-[1.75rem] border border-teal-100 shadow-sm p-5">
          <p className="text-sm text-gray-500">With website</p>
          <p className="text-3xl font-bold text-teal-700 mt-2">{summary.withWebsite}</p>
        </div>
        <div className="bg-white rounded-[1.75rem] border border-purple-100 shadow-sm p-5">
          <p className="text-sm text-gray-500">From Android app</p>
          <p className="text-3xl font-bold text-purple-700 mt-2">{summary.fromApp}</p>
        </div>
        <button
          type="button"
          onClick={() => setStatusFilter('push_on')}
          className="bg-white rounded-[1.75rem] border border-sky-100 shadow-sm p-5 text-left transition hover:bg-sky-50"
        >
          <p className="text-sm text-gray-500">Push Opted-In</p>
          <p className="text-3xl font-bold text-sky-700 mt-2">{summary.pushOptedIn}</p>
          <p className="mt-1 text-xs font-semibold text-sky-700">{summary.pushActiveDevices} active app device(s)</p>
        </button>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-purple-100 p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">Collaborator Categories</p>
            <h3 className="mt-2 text-2xl font-bold text-gray-900">Manage collaborator main categories and sub-categories</h3>
            <p className="mt-2 max-w-3xl text-sm text-gray-600">
              Add more collaborator category groups or extend sub-categories. These settings feed collaborator signup, collaborator account edit, and admin collaborator creation.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-3xl border border-purple-100 bg-purple-50/40 p-4">
            <div className="grid gap-3">
              <input
                value={categoryDraft.name}
                onChange={(event) => handleCategoryDraftChange('name', event.target.value)}
                placeholder="New main category name"
                className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3"
              />
              <textarea
                value={categoryDraft.subcategories_text}
                onChange={(event) => handleCategoryDraftChange('subcategories_text', event.target.value)}
                rows="4"
                placeholder="Sub-categories (comma separated)"
                className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3"
              />
              {categoryError && <p className="text-sm text-red-600">{categoryError}</p>}
              {categoryNotice && <p className="text-sm text-green-700">{categoryNotice}</p>}
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={handleCategorySave} className="btn-primary" disabled={categoryBusy}>
                  {categoryBusy ? 'Saving...' : 'Save category settings'}
                </button>
                <button type="button" onClick={() => setCategoryDraft(emptyCategoryDraft)} className="btn-outline" disabled={categoryBusy}>
                  Clear
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {collaboratorCategoryEntries.map(([name, subcategories]) => (
              <div key={name} className="rounded-3xl border border-gray-200 bg-white p-4">
                <p className="text-sm font-black text-gray-900">{name}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {subcategories.map((subcategory) => (
                    <span key={`${name}-${subcategory}`} className="rounded-full border border-purple-100 bg-purple-50 px-3 py-1 text-xs font-bold text-[#4A148C]">
                      {subcategory}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 p-5 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, email, organisation, city"
            className="w-full rounded-2xl border border-gray-300 px-4 py-3"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white"
          >
            <option value="all">All statuses</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
            <option value="pending_email">Pending email verification</option>
            <option value="review_ready">Ready for review</option>
            <option value="incomplete_signup">Incomplete signup</option>
            <option value="featured">Featured</option>
            <option value="push_on">Push notifications opted-in</option>
            <option value="push_off">Push notifications off/no app device</option>
            <option value="standard">Not featured</option>
          </select>
          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 bg-white"
          >
            <option value="all">All organisation types</option>
            <option value="institutes">Institutes & Academies</option>
            <option value="studios">Studios & Venues</option>
            {collaboratorTypesInList.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 overflow-hidden">
        {messageNotice && <div className="m-5 rounded-2xl bg-green-50 border border-green-200 px-4 py-3 text-green-700 text-sm">{messageNotice}</div>}
        {loading ? (
          <div className="p-6 text-gray-500">Loading collaborators...</div>
        ) : filteredCollaborators.length === 0 ? (
          <div className="p-6 text-gray-500">No collaborators match the current filters.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {filteredCollaborators.map((collaborator) => {
              const isBusy = busyCollaboratorId === collaborator.id;
              const socialProfiles = getCollaboratorSocialProfiles(collaborator);
              const profileAnalysis = analyzeCollaboratorProfile(collaborator);
              const emailVerified = hasVerifiedEmail(collaborator);
              const isReadyForReview = emailVerified && profileAnalysis.score >= 70;
              return (
                <div key={collaborator.id} className="p-5 sm:p-6 space-y-5">
                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-2xl font-semibold text-gray-900">{collaborator.name}</h3>
                        <span className="bg-orange-100 text-[#FF6F00] px-3 py-1 rounded-full text-xs font-semibold">
                          {collaborator.organization_name || 'Organisation pending'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${collaborator.is_verified ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                          {collaborator.is_verified ? 'Verified' : 'Pending'}
                        </span>
                        {!emailVerified && (
                          <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold">
                            Pending email verification
                          </span>
                        )}
                        {emailVerified && <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isReadyForReview ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-[#FF6F00]'}`}>
                          {isReadyForReview ? 'Ready for review' : 'Incomplete signup'}
                        </span>}
                        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${profileScoreBadgeClass(profileAnalysis.tone)}`}>
                          Profile Strength {profileAnalysis.score}/100
                        </span>
                        {collaborator.is_featured && (
                          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Featured
                          </span>
                        )}
                        {collaborator.privacy_policy_accepted && collaborator.terms_accepted && collaborator.trust_safety_accepted && (
                          <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-xs font-semibold">
                            Policies accepted
                          </span>
                        )}
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${hasPushOptIn(collaborator) ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'}`}>
                          {hasPushOptIn(collaborator) ? `Push ON (${collaborator.push_notification_device_count || 1})` : 'Push OFF'}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>{collaborator.email || 'No email added'}</p>
                        {!emailVerified && collaborator.verification_email_last_attempt_at && (
                          <p className={collaborator.verification_email_last_sent ? 'font-semibold text-emerald-700' : 'font-semibold text-red-700'}>
                            Last verification email: {collaborator.verification_email_last_sent ? 'sent' : 'delivery failed'} on {new Date(collaborator.verification_email_last_attempt_at).toLocaleString('en-IN')}
                          </p>
                        )}
                        <p>{collaborator.mobile || 'No mobile added'}</p>
                        <p>{[collaborator.city, collaborator.state].filter(Boolean).join(', ') || 'Location pending'}</p>
                        <p>{collaborator.display_profession || collaborator.organization_type || 'No public profession label'}</p>
                        {collaborator.privacy_policy_accepted && collaborator.terms_accepted && collaborator.trust_safety_accepted && (
                          <p className="font-semibold text-sky-700">
                            Policies accepted{collaborator.policy_accepted_at ? ` on ${new Date(collaborator.policy_accepted_at).toLocaleDateString('en-IN')}` : ''}
                          </p>
                        )}
                      </div>
                      <div className="max-w-4xl rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Social profiles</p>
                          <p className="text-xs font-semibold text-slate-500">
                            {socialProfiles.length ? `${socialProfiles.length} profile${socialProfiles.length > 1 ? 's' : ''} added` : 'No social profile added'}
                          </p>
                        </div>
                        {socialProfiles.length ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {socialProfiles.map((profile) => (
                              <a
                                key={profile.key}
                                href={profile.url}
                                target="_blank"
                                rel="noreferrer"
                                className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold transition hover:-translate-y-0.5 hover:shadow-sm ${profile.tone}`}
                                title={profile.url}
                              >
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/80 text-[10px]">
                                  {profile.shortLabel}
                                </span>
                                <span>{profile.label}</span>
                                <span className="max-w-[180px] truncate font-semibold opacity-80">
                                  {socialProfileDisplayName(profile.url)}
                                </span>
                                {profile.followerLabel && (
                                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide opacity-80">
                                    {profile.followerLabel}
                                  </span>
                                )}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">Website, Instagram, Facebook, or YouTube links have not been added yet.</p>
                        )}
                      </div>
                      {collaborator.bio && <p className="max-w-4xl text-sm text-gray-600">{collaborator.bio}</p>}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      {!emailVerified && collaborator.email && (
                        <CollaboratorActionButton
                          className="border border-amber-200 bg-amber-50 text-amber-800"
                          onClick={() => handleResendEmailVerification(collaborator)}
                          disabled={isBusy}
                        >
                          Resend verification email
                        </CollaboratorActionButton>
                      )}
                      <CollaboratorActionButton
                        className={`border ${collaborator.is_verified ? 'border-green-200 bg-green-50 text-green-700' : 'border-orange-200 bg-orange-50 text-[#FF6F00]'}`}
                        onClick={() => handleVerify(collaborator.id, collaborator.is_verified)}
                        disabled={isBusy}
                      >
                        {collaborator.is_verified ? 'Unverify' : 'Verify'}
                      </CollaboratorActionButton>
                      <CollaboratorActionButton
                        className={`border ${collaborator.is_featured ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-orange-200 bg-orange-50 text-[#FF6F00]'}`}
                        onClick={() => handleFeature(collaborator.id, collaborator.is_featured)}
                        disabled={isBusy}
                      >
                        {collaborator.is_featured ? 'Remove Featured' : 'Feature'}
                      </CollaboratorActionButton>
                      <CollaboratorActionButton
                        className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        onClick={() => handleEdit(collaborator)}
                        disabled={isBusy}
                      >
                        Edit
                      </CollaboratorActionButton>
                      <CollaboratorActionButton
                        className="border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100"
                        onClick={() => {
                          setMessageCollaboratorId((current) => (current === collaborator.id ? '' : collaborator.id));
                          setCollaboratorMessage('');
                          setMessageNotice('');
                        }}
                        disabled={isBusy}
                      >
                        Message
                      </CollaboratorActionButton>
                      {[['3 months', '3 months'], ['6 months', '6 months'], ['1 year', '1 year']].map(([key, label]) => (
                        <CollaboratorActionButton
                          key={`${collaborator.id}-${key}`}
                          className="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          onClick={() => {
                            setMessageCollaboratorId(collaborator.id);
                            setCollaboratorMessage(buildMilestoneMessage(collaborator, label));
                            setMessageNotice('');
                          }}
                          disabled={isBusy}
                        >
                          {label}
                        </CollaboratorActionButton>
                      ))}
                      <a
                        href={collaboratorProfilePath(collaborator)}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
                      >
                        View Profile
                      </a>
                      <CollaboratorActionButton
                        className="border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                        onClick={() => handleDelete(collaborator.id)}
                        disabled={isBusy}
                      >
                        Delete
                      </CollaboratorActionButton>
                    </div>
                  </div>

                  {messageCollaboratorId === collaborator.id && (
                    <div className="rounded-3xl border border-purple-100 bg-purple-50/60 p-4 space-y-3">
                      <p className="text-sm font-semibold text-purple-800">
                        Send profile review note to {collaborator.organization_name || collaborator.name}
                      </p>
                      <textarea
                        value={collaboratorMessage}
                        onChange={(event) => setCollaboratorMessage(event.target.value)}
                        rows="3"
                        placeholder="Example: Please add a clearer website or Instagram link before public verification."
                        className="w-full rounded-2xl border border-purple-200 px-4 py-3"
                      />
                      <div className="flex flex-wrap gap-3">
                        <CollaboratorActionButton
                          className="border border-purple-200 bg-purple-700 text-white hover:bg-purple-800"
                          onClick={() => handleSendAdminMessage(collaborator.id)}
                          disabled={isBusy || !collaboratorMessage.trim()}
                        >
                          Send Message
                        </CollaboratorActionButton>
                        <CollaboratorActionButton
                          className="border border-slate-200 bg-white text-slate-700"
                          onClick={() => {
                            setMessageCollaboratorId('');
                            setCollaboratorMessage('');
                          }}
                          disabled={isBusy}
                        >
                          Cancel
                        </CollaboratorActionButton>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Type</p>
                      <p className="font-semibold text-gray-800 mt-1">{collaborator.display_profession || collaborator.organization_type || 'NA'}</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Website</p>
                      <p className="font-semibold text-gray-800 mt-1">{collaborator.website ? 'Added' : 'Missing'}</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Source</p>
                      <p className="font-semibold text-gray-800 mt-1">{collaborator.sync_source === 'android_app' ? 'App' : 'Website'}</p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Created</p>
                      <p className="font-semibold text-gray-800 mt-1">{formatCreatedAt(collaborator.created_at)}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {getDaysOnKalamanch(collaborator.created_at) === null ? 'Days on KalaManch unavailable' : `${getDaysOnKalamanch(collaborator.created_at)} day(s) on KalaManch`}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-gray-50 px-4 py-3">
                      <p className="text-gray-500">Collaborator ID</p>
                      <p className="mt-1 break-all font-semibold text-gray-800">{collaborator.id}</p>
                    </div>
                    <div className="rounded-2xl bg-sky-50 px-4 py-3 md:col-span-4">
                      <p className="text-sky-700">Push notifications</p>
                      <p className="mt-1 font-semibold text-gray-800">
                        {hasPushOptIn(collaborator)
                          ? `Opted in on ${(collaborator.push_notification_platforms || ['android']).join(', ')}`
                          : 'No active app device or notifications turned off'}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">{formatPushSync(collaborator.push_notification_last_sync_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminCollaborators;
